import { call, put, takeLatest, takeEvery, select } from 'redux-saga/effects';

import {
  getSiteMappings as getSiteMappingsApi,
  createMapping as createMappingApi,
  updateOrDeleteMappings as updateOrDeleteMappingsApi,
  updateMapping as updateMappingApi,
  deleteMapping as deleteMappingApi,
  getGeometries as getGeometriesApi,
  deleteGeometry as deleteGeometryApi,
  createOrUpdateGeometries as createOrUpdateGeometriesApi,
  upsertMappingsInBulk as upsertMappingsInBulkApi,
} from '../../services';
import { displayNotification, checkOnline, getNotification } from '../notifications';
import {
  REQUEST_MAPPINGS,
  CREATE_MAPPING,
  UPDATE_OR_DELETE_MAPPINGS,
  UPDATE_MAPPING,
  DELETE_MAPPING,
  CREATE_MAPPINGS,
  REQUEST_GEOMETRIES,
  CREATE_GEOMETRY_OR_UPDATE,
  DELETE_GEOMETRY,
  CREATE_GEOMETRIES_MAPPINGS,
  requestMappings,
  receiveMappings,
  mappingsImportedAt,
  setImportErrors,
  receiveGeometries,
  requestGeometries,
} from './actions';
import { getVersionMappings } from './selectors';

export function* doRequestMappings(action) {
  try {
    const { siteId, callback, modelId, versionId } = action;
    const { values: mappings } = yield call(getSiteMappingsApi, siteId);
    yield put(receiveMappings(siteId, mappings));

    if (callback && modelId && versionId) {
      const newMappings = yield select((state) =>
        getVersionMappings(state, siteId, modelId, versionId)
      );
      yield call(callback, newMappings);
    }
  } catch (error) {
    console.error('Unable to fetch mapping data:', error);
    yield call(checkOnline);
    yield put(displayNotification(getNotification('getMappings', 'error')()));
  }
}

export function* doCreateMapping(action) {
  const { siteId, modelId, versionId, mapping, callback } = action;

  try {
    yield call(createMappingApi, modelId, versionId, mapping);
    yield put(requestMappings(siteId, { callback, modelId, versionId }));
    yield put(displayNotification(getNotification('createMapping', 'success')(modelId)));
  } catch (error) {
    console.error('Unable to create geometry mapping:', error);
    yield call(checkOnline);
    yield put(displayNotification(getNotification('createMapping', 'error')(modelId)));
  }
}

export function* doCreateMappings(action) {
  const { siteId, modelId, versionId, mappings } = action;

  try {
    const { values: existingMappings } = yield call(getSiteMappingsApi, siteId);
    const filteredMappings = mappings.filter(
      (m) =>
        !existingMappings.some(
          (mapping) =>
            mapping.version_id === versionId &&
            mapping.itemDesignations &&
            JSON.stringify(mapping.itemDesignations) === JSON.stringify(m.itemDesignations) &&
            mapping.dbIds &&
            JSON.stringify(mapping.dbIds) === JSON.stringify(m.dbIds)
        )
    );

    yield upsertMappingsInBulkApi(modelId, versionId, { mappings: filteredMappings });
    yield put(requestMappings(siteId, { callback: () => {}, modelId, versionId }));
    yield put(displayNotification(getNotification('createMapping', 'success')(modelId)));
  } catch (error) {
    console.error('Unable to create geometry mapping:', error);
    yield call(checkOnline);
    yield put(displayNotification(getNotification('createMapping', 'error')(modelId)));
  }
}

export function* doCreateGeometriesAndMappings(action) {
  const { siteId, documentsWithShapes } = action;

  try {
    const { values: existingMappings } = yield call(getSiteMappingsApi, siteId);

    for (const { modelId, versionId, geometries } of documentsWithShapes) {
      // skip creating mappings and geometries if mapping already exist

      const filteredGeometries = geometries.filter(
        (geometry) =>
          !existingMappings.some(
            (mapping) =>
              mapping.version_id === versionId &&
              mapping.itemDesignations &&
              mapping.itemDesignations.length === 1 &&
              mapping.geometryIds &&
              mapping.geometryIds.length === 1 &&
              mapping.itemDesignations[0] === geometry.name
          )
      );

      const {
        data: { values: createdGeometries },
      } = yield call(createOrUpdateGeometriesApi, modelId, { geometries: filteredGeometries });

      const mappings = createdGeometries.map((geometry) => ({
        itemDesignations: [geometry.name],
        geometryIds: [geometry.id],
        dbIds: [],
        position: geometry.position,
      }));

      yield call(upsertMappingsInBulkApi, modelId, versionId, { mappings });
    }

    yield put(mappingsImportedAt());
  } catch (error) {
    console.error('Unable to create geometries and mappings:', error);
    yield call(checkOnline);
    yield put(setImportErrors(['Unable to create all geometries and mappings']));
  }
}

export function* doUpdateOrDeleteMappings(action) {
  const { siteId, modelId, versionId, scope, callback } = action;
  try {
    yield updateOrDeleteMappingsApi(modelId, versionId, scope);
    yield put(requestMappings(siteId));
    yield call(callback);
    yield put(displayNotification(getNotification('clearMappings', 'success')(modelId)));
  } catch (error) {
    console.error('Unable to update/delete geometry mappings:', error);
    yield call(checkOnline);
    yield put(displayNotification(getNotification('clearMappings', 'error')(modelId)));
  }
}

export function* doUpdateMapping(action) {
  const { siteId, modelId, versionId, mapping, callback } = action;

  try {
    yield call(updateMappingApi, modelId, versionId, mapping.id, mapping);
    yield put(requestMappings(siteId, { callback, modelId, versionId }));
    yield put(displayNotification(getNotification('updateMapping', 'success')(modelId)));
  } catch (error) {
    console.error('Unable to update geometry mapping:', error);
    yield call(checkOnline);
    yield put(displayNotification(getNotification('updateMapping', 'error')(modelId)));
  }
}

export function* doDeleteMapping(action) {
  const { siteId, modelId, versionId, mappingId } = action;

  try {
    yield call(deleteMappingApi, modelId, versionId, mappingId);
    yield put(requestMappings(siteId));
    yield put(displayNotification(getNotification('deleteMapping', 'success')(modelId)));
  } catch (error) {
    console.error('Unable to delete geometry mapping:', error);
    yield call(checkOnline);
    yield put(displayNotification(getNotification('deleteMapping', 'error')(modelId)));
  }
}

export function* doRequestGeometries(action) {
  try {
    const { siteId, modelId, callback } = action;
    const { values: geometries } = yield call(getGeometriesApi, modelId);

    yield put(receiveGeometries(siteId, modelId, geometries));
    if (callback) yield call(callback, geometries);
  } catch (error) {
    console.error('Unable to fetch geometry:', error);
    yield call(checkOnline);
    yield put(displayNotification(getNotification('getGeometries', 'error')()));
  }
}

export function* doCreateOrUpdateGeometry(action) {
  const { siteId, modelId, geometries, callback } = action;

  try {
    yield call(createOrUpdateGeometriesApi, modelId, { geometries });
    yield put(requestGeometries(siteId, modelId, callback));
    yield put(displayNotification(getNotification('createGeometry', 'success')(modelId)));
  } catch {
    yield call(checkOnline);
    yield put(displayNotification(getNotification('createGeometry', 'error')(modelId)));
    if (callback) yield call(callback);
  }
}

export function* doDeleteGeometry(action) {
  const { siteId, modelId, geometryId, callback } = action;

  try {
    yield deleteGeometryApi(modelId, geometryId);
    yield put(requestGeometries(siteId, modelId, callback));
    yield put(requestMappings(siteId));
    yield put(displayNotification(getNotification('deleteGeometry', 'success')(modelId)));
  } catch (error) {
    console.error('Unable to delete geometry :', error);
    yield call(checkOnline);
    yield put(displayNotification(getNotification('deleteGeometry', 'error')(modelId)));
    if (callback) yield call(callback);
  }
}

export const sagas = [
  takeEvery(REQUEST_MAPPINGS, doRequestMappings),
  takeLatest(UPDATE_MAPPING, doUpdateMapping),
  takeLatest(CREATE_MAPPING, doCreateMapping),
  takeLatest(CREATE_MAPPINGS, doCreateMappings),
  takeLatest(UPDATE_OR_DELETE_MAPPINGS, doUpdateOrDeleteMappings),
  takeLatest(DELETE_MAPPING, doDeleteMapping),
  takeEvery(REQUEST_GEOMETRIES, doRequestGeometries),
  takeLatest(CREATE_GEOMETRY_OR_UPDATE, doCreateOrUpdateGeometry),
  takeLatest(DELETE_GEOMETRY, doDeleteGeometry),
  takeEvery(CREATE_GEOMETRIES_MAPPINGS, doCreateGeometriesAndMappings),
];
